/**
 * adminNav — the admin menu, one source for both designs.
 * Every item keeps its permission (resource/action) and the per-system visibility of the old sidebar;
 * the new shell only groups them (`group`) and adds a crm-kit/next icon name (`icon`).
 */
export const IS_OPEN_GEMARA = process.env.REACT_APP_EXAM_MODE === 'open-gemara';
export const SYSTEM_ID = String(process.env.REACT_APP_SYSTEM_ID || '').replace(/^["']|["']$/g, '').trim();
export const IS_MISHTAMERET = SYSTEM_ID === 'waiting_client';
export const SYSTEM_LABEL = process.env.REACT_APP_SYSTEM_LABEL || 'מערכת בחינות';

/** משתמרת: בלי הרשמות למבחנים / מבחנים קלאסיים / תשתית הלכתא / דיגלי פיצ'ר / יומן ביקורת */
const HIDDEN_NAV_FOR_WAITING_CLIENT = new Set([
  '/kolels',
  '/gabbai-orders',
  '/labels',
  '/registrations',
  '/exams',
  '/exam-periods',
  '/exam-cities',
  '/exam-library',
  '/audit-log',
  '/feature-flags',
]);

const FRAMES = [
  { key: 'daf_yomi', label: 'דף היומי' },
  { key: 'shinun', label: 'דף השינון' },
  { key: 'meshannim', label: 'משננים' },
];

export const NAV_ITEMS = [
  { id: 'dashboard', label: 'דף הבית', path: '/dashboard', icon: 'home', group: 'top', resource: 'reports', action: 'read' },
  { id: 'calendar', label: 'לוח שנה', path: '/calendar', icon: 'calendar', group: 'top', resource: 'studyCalendar', action: 'read' },
  { id: 'members', label: 'חברים', path: '/members', icon: 'users', group: 'people', resource: 'members', action: 'read' },
  ...(!IS_OPEN_GEMARA && !IS_MISHTAMERET ? [{ id: 'exams', label: 'מבחנים', path: '/exams', icon: 'task', group: 'exams', resource: 'exams', action: 'read' }] : []),
  ...(IS_MISHTAMERET ? [{
    id: 'american-exams',
    label: 'מבחנים אמריקאים',
    path: '/american-exams',
    icon: 'check',
    group: 'american',
    resource: 'exams',
    action: 'read',
    children: FRAMES.map((f) => ({ id: `american-exams-${f.key}`, label: f.label, path: `/american-exams/${f.key}` })),
  }] : []),
  ...(IS_MISHTAMERET ? [{
    id: 'examinees',
    label: 'נבחנים',
    path: '/examinees',
    icon: 'checkAll',
    group: 'examinees',
    resource: 'grades',
    action: 'read',
    children: [
      ...FRAMES.map((f) => ({ id: `examinees-${f.key}`, label: f.label, path: `/examinees/${f.key}` })),
      { id: 'examinees-report', label: 'דו"ח נבחנים', path: '/examinees/report' },
    ],
  }] : []),
  ...(IS_MISHTAMERET ? [{ id: 'gemara-orders', label: 'הזמנות גמרא', path: '/gemara-orders', icon: 'book', group: 'exams', resource: 'exams', action: 'read' }] : []),
  { id: 'kolels', label: 'כוללים', path: '/kolels', icon: 'building', group: 'people', resource: 'kolels', action: 'read' },
  ...(!IS_MISHTAMERET ? [{ id: 'registrations', label: 'הרשמות למבחנים', path: '/registrations', icon: 'note', group: 'exams', resource: 'examRegistration', action: 'read' }] : []),
  ...(!IS_MISHTAMERET ? [{ id: 'exam-periods', label: 'זמני מבחנים', path: '/exam-periods', icon: 'calendar', group: 'exams', resource: 'examPeriod', action: 'read' }] : []),
  ...(!IS_MISHTAMERET ? [{ id: 'exam-cities', label: 'ערי מבחן', path: '/exam-cities', icon: 'flag', group: 'exams', resource: 'examCity', action: 'read' }] : []),
  ...(IS_MISHTAMERET ? [{ id: 'broadcasts', label: 'שיגורי הודעות', path: '/broadcasts', icon: 'mic', group: 'people', resource: 'members', action: 'update' }] : []),
  { id: 'pending-registrations', label: 'בקשות רישום חדשים', path: '/pending-registrations', icon: 'inbox', group: 'people', resource: 'members', action: 'create' },
  ...(!IS_OPEN_GEMARA ? [{ id: 'gabbai-orders', label: 'הזמנות גבאים', path: '/gabbai-orders', icon: 'box', group: 'exams', resource: 'exams', action: 'read' }] : []),
  ...(!IS_MISHTAMERET ? [{ id: 'exam-library', label: 'ספריית בחינות', path: '/exam-library', icon: 'book', group: 'exams', resource: 'exams', action: 'read' }] : []),
  { id: 'labels', label: 'מדבקות וברקודים', path: '/labels', icon: 'hash', group: 'exams', resource: 'labels', action: 'read' },
  { id: 'content', label: 'תוכן', path: '/content', icon: 'play', group: 'content', resource: 'messages', action: 'read' },
  { id: 'narration-catalog', label: 'קטלוג קריינות', path: '/narration-catalog', icon: 'mic', group: 'content', resource: 'narrationPrompts', action: 'read' },
  { id: 'ivr', label: 'IVR', path: '/ivr', icon: 'phone', group: 'content', resource: 'ivr', action: 'read' },
  ...(IS_MISHTAMERET
    ? [{ id: 'ivr-templates', label: 'תבניות IVR', path: '/ivr-templates', icon: 'phone', group: 'content', resource: 'ivr', action: 'read' }]
    : []),
  ...(IS_MISHTAMERET
    ? [{ id: 'email-automations', label: 'מיילים אוטומטיים', path: '/email-automations', icon: 'mail', group: 'content', resource: 'emailDomains', action: 'read' }]
    : []),
  { id: 'subscriptions', label: 'מנויים', path: '/subscriptions', icon: 'bell', group: 'content', resource: 'subscriptions', action: 'read' },
  { id: 'reports', label: 'דוחות', path: '/reports', icon: 'chart', group: 'reports', resource: 'reports', action: 'read' },
  { id: 'audit-log', label: 'יומן ביקורת', path: '/audit-log', icon: 'history', group: 'settings', resource: 'auditLogs', action: 'read' },
  { id: 'feature-flags', label: 'Feature Flags', path: '/feature-flags', icon: 'flag', group: 'settings', resource: 'featureFlags', action: 'read' },
  { id: 'email-domain', label: 'דומיין לשליחת מיילים', path: '/email-domain', icon: 'mail', group: 'settings', resource: 'emailDomains', action: 'read' },
  { id: 'roles', label: 'תפקידים', path: '/roles', icon: 'key', group: 'settings', adminOnly: true, resource: 'roles', action: 'read' },
  { id: 'users', label: 'משתמשים', path: '/users', icon: 'users', group: 'settings', adminOnly: true, resource: 'users', action: 'read' },
];

/** the items this user may open, in menu order */
export function visibleNavItems(user, hasPermission) {
  return NAV_ITEMS.filter((item) => {
    if (IS_MISHTAMERET && HIDDEN_NAV_FOR_WAITING_CLIENT.has(item.path)) return false;
    if (item.adminOnly && !user?.roles?.includes('admin')) return false;
    if (item.resource && item.action && !hasPermission(item.resource, item.action)) return false;
    return true;
  });
}

export const isPathActive = (pathname, path) => pathname === path || pathname.startsWith(`${path}/`);
